import React, { useState } from 'react'
import { ClickAwayListener, Grid, useMediaQuery } from '@mui/material'
import makeStyles from '@mui/styles/makeStyles'
import { LoveList as LoveListIcon } from '@pbt/pbt-ui-components/src/icons'

import { AlertColorMap } from '~/constants/alertColors'
import type { PatientPreferencesDisplayData } from '~/types'
import type { ClientPreferencesDisplayData } from '~/types/entities/clientPreferences'

import { PreferencesDisplayWidget } from '../dashboard/preferences/PreferencesDisplayWidget'
import RhapPreferencesDisplayWidget from '../dashboard/preferences/RhapPreferencesDisplayWidget'
import TooltipChip from './TooltipChip'

const useStyles = makeStyles(
  (theme) => ({
    icon: {
      width: '100%',
      height: '100%',
      fill: theme.colors.alertErrorText,
    },
  }),
  { name: 'PreferencesChip' },
)

export interface PreferencesChipProps {
  className?: string
  headerText: string
  isDisplayWidgetChirp?: boolean
  onEditPreferences?: () => void
  preferences?: ClientPreferencesDisplayData | PatientPreferencesDisplayData
}

function PreferencesChip({
  className,
  headerText,
  preferences,
  onEditPreferences = () => {},
  isDisplayWidgetChirp,
}: PreferencesChipProps) {
  const classes = useStyles()
  const isTouch = useMediaQuery('(pointer:coarse)')

  const [open, setOpen] = useState(false)

  const onEditPreferencesClick = () => {
    setOpen(false)
    onEditPreferences()
  }

  const hasPreferences = Object.values(preferences || {}).some(Boolean)

  const widgetToDisplay = isDisplayWidgetChirp ? (
    <RhapPreferencesDisplayWidget
      headerText={headerText}
      preferences={preferences}
      onEditPreferences={onEditPreferencesClick}
    />
  ) : (
    <PreferencesDisplayWidget
      headerText={headerText}
      preferences={preferences}
      onEditPreferences={onEditPreferencesClick}
    />
  )

  const Component = (
    <TooltipChip
      disableTooltipBranding
      PopperProps={{
        disablePortal: isDisplayWidgetChirp,
      }}
      className={className}
      color={hasPreferences ? AlertColorMap.BLUE : AlertColorMap.GREY}
      icon={<LoveListIcon className={classes.icon} />}
      open={open}
      tooltipPlacement="bottom-start"
      tooltipText={widgetToDisplay}
      onClose={() => {
        if (!isTouch) setOpen(false)
      }}
      onOpen={() => setOpen(true)}
    />
  )

  return isTouch ? (
    <ClickAwayListener onClickAway={() => setOpen(false)}>
      <Grid>{Component}</Grid>
    </ClickAwayListener>
  ) : (
    Component
  )
}

export { PreferencesChip }
